import { CircleIcon, FilterIcon } from '@assets/icons';
import { Checkbox } from '@components/form';
import { Group } from '@components/layout';
import { SearchDetail } from '@generated/graphql';
import { Box, Menu, MenuItem, capitalize, useTheme } from '@mui/material';
import { useRef, useState } from 'react';
import { DateFilter } from './DateFilter';
import { MultiSelectFilter } from './MultiSelectFilter';
import { NumberFilter } from './NumberFilter';
import { NumberRangeFilter } from './NumberRangeFilter';
import { SizeFilter } from './SizeFilter';
import { TextFilter } from './TextFilter';
import {
  DeckardFilter,
  Filter,
  FilterApply,
  FilterComponent,
  FilterType,
  SearchDetailTypes,
} from './types';

type FiltersMenuProps<T> = {
  filter: DeckardFilter<T>;
  searchDetail: SearchDetailTypes;
  sort?: boolean;
  variant: 'primary' | 'secondary';
  onFilterChange: (newFilter: DeckardFilter<T>) => void;
};

export const FiltersMenu = <T,>({
  filter,
  searchDetail,
  sort,
  variant,
  onFilterChange,
}: FiltersMenuProps<T>) => {
  const { palette } = useTheme();

  const updateFilter = (key: string, value: unknown, display: string[]) => {
    onFilterChange({
      ...filter,
      [key]: { ...filter[key], value, display } as DeckardFilter<T>[keyof DeckardFilter<T>],
    });
  };

  const [activeMenu, setActiveMenu] = useState<
    'filters' | 'multiSelect' | 'date' | 'number' | 'numberRange' | 'size' | 'text' | null
  >(null);

  const anchorEl = useRef<HTMLDivElement>(null);

  const [activeMenuState, setActiveMenuState] = useState<{
    component: FilterComponent<unknown>;
    title: string;
    field: string;
    filter: Filter<unknown>;
    details: SearchDetail[];
  } | null>(null);

  const handleFilterClickNew = (key: string) => {
    const filterType: FilterType = (filter[key] as Filter<unknown>).type;
    const newActiveMenuState = {
      field: key,
      title: (filter[key] as Filter<unknown>).label ?? capitalize(key),
      filter: filter[key] as Filter<unknown>,
      details: searchDetail[key] as SearchDetail[],
      component: null,
    };
    if (['exact', 'string'].includes(filterType)) {
      setActiveMenu('multiSelect');
      setActiveMenuState({
        ...newActiveMenuState,
        component: MultiSelectFilter,
      });
    } else if (filterType === 'date') {
      setActiveMenu('date');
      setActiveMenuState({
        ...newActiveMenuState,
        component: DateFilter,
      });
    } else if (filterType === 'number') {
      setActiveMenu('number');
      setActiveMenuState({
        ...newActiveMenuState,
        component: NumberFilter,
      });
    } else if (filterType === 'numberRange') {
      setActiveMenu('numberRange');
      setActiveMenuState({
        ...newActiveMenuState,
        component: NumberRangeFilter,
      });
    } else if (filterType === 'size') {
      setActiveMenu('size');
      setActiveMenuState({
        ...newActiveMenuState,
        component: SizeFilter,
      });
    } else if (filterType === 'text') {
      setActiveMenu('text');
      setActiveMenuState({
        ...newActiveMenuState,
        component: TextFilter,
      });
    }
  };

  const onApply: FilterApply = ({ value, display = [], close = true }) => {
    updateFilter(activeMenuState.field, value, display);
    if (close) {
      setActiveMenu(null);
      setActiveMenuState(null);
    }
  };
  const onBack = () => {
    setActiveMenu('filters');
    setActiveMenuState(null);
  };

  return (
    <>
      <Box
        ref={anchorEl}
        py={1}
        px={1.5}
        display="flex"
        alignItems="center"
        onClick={() => {
          setActiveMenu(activeMenu ? null : 'filters');
        }}
        style={{ cursor: 'pointer', outline: 'none' }}
        border="1px solid"
        borderColor={palette.table.modeButton.border.selected}
        sx={{
          borderRadius: sort ? 0 : '0 3px 3px 0',
          backgroundColor:
            variant === 'primary' ? palette.background.paper : palette.input.background.default,
          borderLeftWidth: 0,
          '& .deckard-icon': {
            color: palette.table.modeButton.icon.default,
          },
          '&:hover': {
            backgroundColor: palette.table.modeButton.background.hover,
            '& .deckard-icon': { color: palette.table.modeButton.icon.hover },
          },
          '&:active': {
            backgroundColor: palette.table.modeButton.border.selected,
          },
        }}
      >
        <FilterIcon />
      </Box>
      <Menu
        anchorEl={anchorEl.current}
        open={Boolean(activeMenu)}
        disablePortal
        onClose={() => setActiveMenu(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {!activeMenu ? null : activeMenu === 'filters' ? (
          Object.entries<Filter<unknown>>(filter)
            .filter(
              ([key]) =>
                key !== 'texts' &&
                (!searchDetail?.[key] || (searchDetail?.[key] as SearchDetail[]).length > 0),
            )
            .map(([key, value]) =>
              value.type === 'boolean' ? (
                <MenuItem key={key}>
                  <Group
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    sx={{ width: '100%' }}
                    onClick={() => updateFilter(key, !value.value, [])}
                  >
                    {value.label ?? capitalize(key)}
                    <Checkbox checked={value.value} sx={{ height: 'auto' }} />
                  </Group>
                </MenuItem>
              ) : (
                <MenuItem key={key} onClick={() => handleFilterClickNew(key)}>
                  <Group
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    sx={{ width: '100%' }}
                  >
                    {value.label ?? capitalize(key)}
                    {((filter[key] as Filter<unknown[]>).value as []).length > 0 && (
                      <CircleIcon
                        color={
                          palette.mode === 'dark' ? palette.primary.main : palette.primary.dark
                        }
                        size={6}
                        style={{ marginLeft: '5px', marginRight: '5px' }}
                      />
                    )}
                  </Group>
                </MenuItem>
              ),
            )
        ) : (
          <activeMenuState.component {...activeMenuState} onApply={onApply} onBack={onBack} />
        )}
      </Menu>
    </>
  );
};
